import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams, useLocation } from "react-router-dom";
import { FaCheckCircle } from 'react-icons/fa';
import TopHeader from '../../Header/TopHeader';
import Navbar from '../../Header/Navbar';
import Footer from '../../Footer/Footer';
import Breadcrumb from '../../Screens/Breadcrumb';
import PopularService from '../PopularService';
import '../../Css/PropertiesShip.css';
import '../../Css/DocumentRequire.css';
import company_law_compliance from '../../Images/Banners/company_law_compliance.png';
import fssai_registration from '../../Images/Banners/fssai_registration.png';
import trademark_registration from '../../Images/Banners/trademark_registration.png';
import iso_certification from '../../Images/Banners/iso_certification.png';
import llp_law_compliance from '../../Images/Banners/llp_law_compliance.png';
import din_application from '../../Images/Banners/din_application.png';
import epf_registration from '../../Images/Banners/epf_registration.png';
import esi_registration from '../../Images/Banners/esi_registration.png';
import esi_benefits from '../../Images/Banners/esi_benefits.png';
import business_planning from '../../Images/Banners/business_planning.png';
import business_loan from '../../Images/Banners/business_loan.png';
import cc_overdraft from '../../Images/Banners/cc_overdraft.png';
import personal_loan from '../../Images/Banners/personal_loan.png';

const parseServiceDescription = (html) => {
    if (!html) return { about: '', documents: '' };
    const docSplit = html.split(/<h3[^>]*>.*?Documents.*?<\/h3>/i);
    if (docSplit.length > 1) {
        return { about: docSplit[0], documents: docSplit[1] };
    }
    return { about: html, documents: '' };
};

const getHeroImage = (title) => {
    const slug = title?.toLowerCase().trim() || "";
    if (slug.includes('fssai') || slug.includes('food')) return fssai_registration;
    if (slug.includes('trademark') || slug.includes('brand') || slug.includes('copyright')) return trademark_registration;
    if (slug.includes('iso')) return iso_certification;
    if (slug.includes('company law') || slug.includes('add director') || slug.includes('remove director') || slug.includes('roc')) return company_law_compliance;
    if (slug.includes('llp')) return llp_law_compliance;
    if (slug.includes('din') || slug.includes('dsc')) return din_application;
    if (slug.includes('epf') || slug.includes('pf') || slug.includes('provident')) return epf_registration;
    if (slug.includes('esi') && slug.includes('benefit')) return esi_benefits;
    if (slug.includes('esi') || slug.includes('state insurance')) return esi_registration;
    if (slug.includes('business planning') || slug.includes('msme') || slug.includes('udyog') || slug.includes('startup')) return business_planning;
    if (slug.includes('business loan')) return business_loan;
    if (slug.includes('cc') || slug.includes('overdraft')) return cc_overdraft;
    if (slug.includes('personal loan') || slug.includes('home loan') || slug.includes('education loan') || slug.includes('vehicle loan') || slug.includes('loan against property') || slug.includes('inter corporate loans')) return personal_loan;
    if (slug.includes('project report') || slug.includes('investment')) return business_planning;
    if (slug.includes('income tax') || slug.includes('itr') || slug.includes('tds') || slug.includes('gst')) return business_planning;
    if (slug.includes('import') || slug.includes('export') || slug.includes('iec')) return company_law_compliance;
    if (slug.includes('tax') || slug.includes('audit')) return business_planning;
    if (slug.includes('license') || slug.includes('certification') || slug.includes('psara')) return iso_certification;
    if (slug.includes('legal') || slug.includes('law') || slug.includes('compliance')) return company_law_compliance;
    if (slug.includes('registration')) return iso_certification;
    return company_law_compliance;
}

function Gst() {
    const { slug } = useParams();
    const location = useLocation();
    const id = location.state?.id;
    const serviceName = location.state?.serviceName;

    const [processData, setProcessData] = useState([]);
    const [serviceData, setServiceData] = useState({});
    const [servicesList, setServicesList] = useState([]);
    const [loading, setLoading] = useState(true);

    const getProcessData = async () => {
        try {
            const { data } = await axios.get(`https://api.compliancekare.com/api/Compliance/RegistrationProcessList?BusinessId=${id}`);
            setProcessData(data.data || []);
        } catch (error) {
            console.error("Error fetching process data:", error);
        }
    };

    const getServiceData = async () => {
        try {
            const { data } = await axios.get(`https://api.compliancekare.com/api/Compliance/GSTDetailList?GSTId=${id}`);
            if (!data || !data.id) {
                throw new Error("No data in GSTDetailList, triggering fallback");
            }
            setServiceData(data.id || {});
            setServicesList(data.services || []);
        } catch (error) {
            try {
                // Fallback to general ServiceDetailList if specific endpoint fails (e.g., 404 for PAN Application)
                const { data } = await axios.get(`https://api.compliancekare.com/api/Compliance/ServiceDetailList?BusinessId=${id}`);
                setServiceData(data?.id || {});
                setServicesList(data?.services || []);
            } catch (fallbackError) {
                console.error("Error fetching service data:", fallbackError);
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            if (id) {
                await Promise.all([getProcessData(), getServiceData()]);
            }
            setLoading(false);
        };
        fetchData();
    }, [id]);

    const title = serviceName || serviceData.gstTypeName || "GST";
    const titleLower = title.toLowerCase();

    const getHeroContent = () => {
        if (titleLower.includes('registration')) {
            return {
                eyebrow: "Start Your Journey with",
                heading: "GST Registration",
                tagline: "Get Your Business GST Compliant Fast",
                features: [
                    { icon: "bi-rocket-takeoff", title: "Quick Process", desc: "Hassle-Free Registration" },
                    { icon: "bi-shield-check", title: "100% Compliant", desc: "Meets All Legal Needs" },
                    { icon: "bi-building", title: "Business Growth", desc: "Unlock Market Opportunities" }
                ],
                image: '/images/gst_services_hero.png'
            };
        }
        if (titleLower.includes('return')) {
            return {
                eyebrow: "Simplify Your Filings",
                heading: "GST Return",
                tagline: "Accurate & Timely GST Return Filing",
                features: [
                    { icon: "bi-calculator", title: "Accurate Filing", desc: "Error-Free Computations" },
                    { icon: "bi-calendar-check", title: "Timely Submission", desc: "Never Miss a Deadline" },
                    { icon: "bi-check2-all", title: "Full Compliance", desc: "Avoid Late Penalties" }
                ],
                image: '/images/gst_services_hero.png'
            };
        }
        if (titleLower.includes('refund')) {
            return {
                eyebrow: "Claim Your Dues",
                heading: "GST Refund",
                tagline: "Fast & Secure GST Refund Processing",
                features: [
                    { icon: "bi-cash-coin", title: "Maximum Claims", desc: "Recover Eligible Refunds" },
                    { icon: "bi-speedometer2", title: "Fast Processing", desc: "Expedited Applications" },
                    { icon: "bi-shield-lock", title: "Secure Handing", desc: "Expert Documentation" }
                ],
                image: '/images/gst_services_hero.png'
            };
        }
        if (titleLower.includes('notice')) {
            return {
                eyebrow: "Expert Resolution for",
                heading: "GST Notice",
                tagline: "Professional Handling of Department Notices",
                features: [
                    { icon: "bi-envelope-exclamation", title: "Prompt Reply", desc: "Timely Notice Response" },
                    { icon: "bi-person-vcard", title: "Expert Assistance", desc: "CA Supported Actions" },
                    { icon: "bi-shield-shaded", title: "Risk Mitigation", desc: "Protect Your Business" }
                ],
                image: '/images/gst_services_hero.png'
            };
        }
        if (titleLower.includes('demand')) {
            return {
                eyebrow: "Settle Department Claims",
                heading: "GST Demand",
                tagline: "Resolve GST Demands & Discrepancies",
                features: [
                    { icon: "bi-journal-medical", title: "Detailed Analysis", desc: "Assess Demand Validity" },
                    { icon: "bi-hammer", title: "Appeals Support", desc: "Legal Recourse Available" },
                    { icon: "bi-check-circle-fill", title: "Final Resolution", desc: "Clear Outstanding Dues" }
                ],
                image: '/images/gst_services_hero.png'
            };
        }
        if (titleLower.includes('surrender') || titleLower.includes('cancellation')) {
            return {
                eyebrow: "Smooth Exit Strategy",
                heading: "GST Surrender",
                tagline: "Hassle-Free Cancellation of GST Registration",
                features: [
                    { icon: "bi-power", title: "Easy Surrender", desc: "Streamlined Process" },
                    { icon: "bi-ui-checks", title: "Final Returns", desc: "Clear Pending Filings" },
                    { icon: "bi-shield-check", title: "Safe Closure", desc: "No Future Liabilities" }
                ],
                image: '/images/gst_services_hero.png'
            };
        }

        // Default
        return {
            eyebrow: "Comprehensive Solutions for",
            heading: title,
            tagline: "Fast, Secure & Hassle-Free GST Services",
            features: [
                { icon: "bi-bar-chart", title: "Expert Service", desc: "Specialized GST Support" },
                { icon: "bi-shield-check", title: "100% Secure", desc: "Safe Data Processing" },
                { icon: "bi-award", title: "Guaranteed Success", desc: "Client Satisfaction" }
            ],
            image: company_law_compliance
        };
    };

    const heroContent = getHeroContent();
    heroContent.image = getHeroImage(title);

    return (
        <>
            <TopHeader />
            <Navbar />

            <div className="proprietorship-hero-banner">
                <div className="container h-100 position-relative z-index-2">
                    <div className="row h-100 align-items-center py-5">
                        {/* LEFT SIDE (TEXT) */}
                        <div className="col-lg-8 text-white text-start">
                            <div className="d-flex align-items-center mb-2">
                                <div className="hero-eyebrow-line"></div>
                                <span className="hero-eyebrow-text ms-2">{heroContent.eyebrow}</span>
                            </div>
                            <h1 className="hero-main-heading">{heroContent.heading}</h1>
                            <p className="hero-tagline mb-4">{heroContent.tagline}</p>

                            <div className="hero-features-container mt-4">
                                {heroContent.features.map((feature, idx) => (
                                    <div className="hero-feature-card" key={idx}>
                                        <div className="hero-feature-icon">
                                            <i className={`bi ${feature.icon}`}></i>
                                        </div>
                                        <div className="hero-feature-text">
                                            <h6 className="mb-0 text-white fw-bold">{feature.title}</h6>
                                            <small className="text-light-blue">{feature.desc}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT SIDE (IMAGE) */}
                        <div className="col-lg-4 d-none d-lg-flex align-items-center justify-content-center position-relative">
                            <div className="hero-dynamic-image-wrapper">
                                <img src={heroContent.image} alt={heroContent.heading} className="img-fluid" style={{ maxHeight: '420px', objectFit: 'contain', zIndex: 5, position: 'relative', filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.5))', borderRadius: '15px' }} />
                            </div>
                        </div>
                    </div>
                </div>
                {/* Background decorations */}
                <div className="hero-bg-glow"></div>
                <div className="hero-bg-wave"></div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                        .proprietorship-hero-banner {
                            background: linear-gradient(135deg, #09304e 0%, #0d4670 100%);
                            position: relative;
                            overflow: hidden;
                            min-height: 450px;
                            display: flex;
                            align-items: center;
                        }
                        .hero-bg-glow {
                            position: absolute;
                            top: -20%;
                            left: 10%;
                            width: 600px;
                            height: 600px;
                            background: radial-gradient(circle, rgba(137, 199, 100, 0.15) 0%, rgba(0,0,0,0) 70%);
                            z-index: 1;
                        }
                        .hero-bg-wave {
                            position: absolute;
                            bottom: 0;
                            left: 0;
                            width: 100%;
                            height: 100px;
                            background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg"><path fill="%2389c764" fill-opacity="0.2" d="M0,256L80,245.3C160,235,320,213,480,213.3C640,213,800,235,960,240C1120,245,1280,235,1360,229.3L1440,224L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"></path></svg>') no-repeat bottom;
                            background-size: cover;
                            z-index: 1;
                        }
                        .z-index-2 {
                            z-index: 2;
                        }
                        .hero-eyebrow-line {
                            width: 40px;
                            height: 2px;
                            background-color: #89c764;
                        }
                        .hero-eyebrow-text {
                            font-size: 1.1rem;
                            color: #f1f5f9;
                            letter-spacing: 0.5px;
                        }
                        .hero-main-heading {
                            font-size: 3.2rem;
                            white-space: nowrap;
                            font-weight: 800;
                            color: #ffffff;
                            text-shadow: 0 4px 15px rgba(0,0,0,0.2);
                            margin: 5px 0 10px 0;
                            line-height: 1.1;
                        }
                        .hero-tagline {
                            font-size: 1.3rem;
                            color: #e2e8f0;
                            font-weight: 500;
                        }
                        .text-lime {
                            color: #89c764;
                        }
                        .text-light-blue {
                            color: #cbd5e1;
                            font-size: 0.85rem;
                        }
                        .hero-breadcrumb {
                            font-size: 0.95rem;
                            color: #cbd5e1;
                        }
                        .hero-features-container {
                            display: flex;
                            gap: 15px;
                            flex-wrap: nowrap;
                        }
                        .hero-feature-card {
                            background: rgba(13, 70, 112, 0.6);
                            border: 1px solid rgba(137, 199, 100, 0.3);
                            backdrop-filter: blur(10px);
                            border-radius: 12px;
                            padding: 10px 12px;
                            display: flex;
                            align-items: center;
                            gap: 15px;
                            flex: 1;
                            min-width: 0;
                            transition: transform 0.3s ease, background 0.3s ease;
                        }
                        .hero-feature-card:hover {
                            transform: translateY(-5px);
                            background: rgba(13, 70, 112, 0.9);
                            border-color: #89c764;
                        }
                        .hero-feature-icon {
                            font-size: 20px;
                            color: #89c764;
                            background: rgba(137, 199, 100, 0.1);
                            width: 40px;
                            height: 40px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border-radius: 50%;
                            flex-shrink: 0;
                            border: 1px solid rgba(137, 199, 100, 0.2);
                        }
                        @media (max-width: 991px) {
                            .hero-features-container { flex-wrap: wrap; }
                            .hero-feature-card { min-width: 200px; }
                            .hero-main-heading { font-size: 2.2rem; white-space: normal; word-break: keep-all; }
                        }
                    `}} />
            <div className="service-page-wrapper py-5">
                <div className="container">
                    <div className="row g-4">
                        {/* MAIN CONTENT (FULL WIDTH) */}
                        <div className="col-lg-12">
                            {(() => {
                                const parsedHtml = parseServiceDescription(serviceData.proprietorshipDescription || serviceData.gstDescription);
                                return (
                                    <>
                                        {/* ABOUT SECTION */}
                                        <div className="modern-about-section">
                                            <div className="modern-about-content">
                                                <h2 className="mb-3">About {title}</h2>
                                                <div dangerouslySetInnerHTML={{ __html: parsedHtml.about }}></div>
                                            </div>
                                            <div className="modern-about-img">
                                                <img src={heroContent.image} alt={title} className="img-fluid" style={{ borderRadius: '12px', maxHeight: '250px', objectFit: 'contain' }} />
                                            </div>
                                        </div>

                                        {/* SPLIT SECTION (DOCS + PRICING) */}
                                        <div className="modern-split-section">
                                            {/* DOCUMENTS (Left) */}
                                            {parsedHtml.documents ? (
                                                <div className="modern-docs-col">
                                                    <div className="modern-docs-section">
                                                        <h4>List of Documents Required</h4>
                                                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Keep these documents ready for {title}:</p>
                                                        <div className="pg_content" dangerouslySetInnerHTML={{ __html: parsedHtml.documents }}></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="modern-docs-col" style={{ display: 'none' }}></div>
                                            )}

                                            {/* PRICING (Right) */}
                                            <div className="modern-pricing-col">
                                                <div className="modern-pricing-card">
                                                    <div className="pricing-snapshot-title">
                                                        <i className="bi bi-tag-fill" style={{ color: '#10b981' }}></i>
                                                        Pricing Snapshot
                                                        <span className="pricing-badge">
                                                            <i className="bi bi-stars"></i> Transparent & Affordable
                                                        </span>
                                                    </div>

                                                    <div className="pricing-grid">
                                                        <div className="pricing-grid-item">
                                                            <span className="pricing-label">Market Price</span>
                                                            <span className="pricing-val market-price">₹{serviceData.marketPrice || '0'}.00</span>
                                                        </div>
                                                        <div className="pricing-grid-item border-sides">
                                                            <span className="pricing-label">Our Price</span>
                                                            <span className="pricing-val our-price">₹{serviceData.price || serviceData.packageFee || '0'}.00</span>
                                                        </div>
                                                        <div className="pricing-grid-item">
                                                            <span className="pricing-label">You Save</span>
                                                            <span className="pricing-val you-save">₹{serviceData.savePrice || '0'}.00</span>
                                                        </div>
                                                    </div>

                                                    <Link to="/contact" className="get-started-btn">
                                                        <i className="bi bi-cart3"></i> Get Started Now <i className="bi bi-chevron-right ms-2"></i>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>

                                        {/* PACKAGE INCLUDES */}
                                        {servicesList && servicesList.length > 0 && (
                                            <div className="modern-package-section">
                                                <div className="package-title-wrapper">
                                                    <div className="package-icon">
                                                        <i className="bi bi-gift-fill"></i>
                                                    </div>
                                                    <div>
                                                        <h3>Package Includes</h3>
                                                        <p>Everything you need for a hassle-free setup</p>
                                                    </div>
                                                </div>
                                                <div className="package-cards-grid">
                                                    {servicesList.map((item, index) => (
                                                        <div className="modern-package-card" key={index}>
                                                            <i className="bi bi-check-circle-fill package-check"></i>
                                                            <div className="package-text">
                                                                <h6>{item.service}</h6>
                                                                <p>End-to-end support & processing</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* REGISTRATION PROCESS */}
                                        {processData && processData.length > 0 && (
                                            <div className="modern-process-section mt-4 mb-5">
                                                <div className="section-header">
                                                    <h3><i className="bi bi-gear-fill text-primary me-2"></i>Registration Process</h3>
                                                    <p>Simple Steps to Get Your Business Registered</p>
                                                </div>
                                                <div className="process-flow">
                                                    {processData.map((step, index) => (
                                                        <div className="process-step-card" key={index}>
                                                            <div className="process-step-badge">Step {index + 1}</div>
                                                            <div className="process-step-icon">
                                                                <i className="bi bi-check2-circle"></i>
                                                            </div>
                                                            <h6 className="process-step-title">{step.processName || `Day ${index + 1}`}</h6>
                                                            <div className="process-step-desc mt-2 text-muted" style={{ fontSize: "14px" }} dangerouslySetInnerHTML={{ __html: step.registrationProcess || 'Our experts will handle this step for your application.' }}></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            <PopularService />
            <Footer />
        </>
    );
}

export default Gst;